import { Controller } from "@hotwired/stimulus"
import { get, post } from "@rails/request.js"

/**
 * ActionBuilderController - Manages dynamic action sequence building
 *
 * Inspired by Zapier/n8n action configuration:
 * - Modal-based action selector with categories and search
 * - Visual action cards with type-specific forms
 * - Drag-and-drop reordering
 * - Real-time JSON serialization
 * - Scalable for 50+ action types
 */

const FOCUS_DELAY_MS = 100
const SEARCH_DEBOUNCE_MS = 150

const CATEGORY_ALL = "all"

export default class extends Controller {
  static targets = [
    "output",
    "actions",
    "emptyState",
    "modal",
    "searchInput",
    "actionsGrid",
    "categoryButton",
    "noResults",
    "errorMessage",
    "deleteModal",
    "cardTemplate",
  ]
  static values = {
    actionTypes: Object,
    categories: Object,
    fields: Array,
    models: Array,
    value: Array,
    maxActions: { type: Number, default: 10 },
    disabled: { type: Boolean, default: false },
    recordFormUrl: { type: String, default: "" },
    fieldsUrl: { type: String, default: "" },
    associationRecordsUrl: { type: String, default: "" },
    validateUrl: { type: String, default: "" },
    pipeFunctions: { type: Array, default: [] },
    pipeMenuLabels: { type: Object, default: {} },
  }

  connect() {
    this.updateOutput()
    this.selectedCategory = CATEGORY_ALL
    this.searchTimeout = null
    this._triggerFieldsFull = []

    const form = this.element.closest("form")
    if (form) {
      this.handleFormSubmit = this.handleFormSubmit.bind(this)
      form.addEventListener("submit", this.handleFormSubmit)
    }

    // Delegated inline {{expression}} validation on blur of any config input
    this.handleFocusOut = this.handleFocusOut.bind(this)
    this.element.addEventListener("focusout", this.handleFocusOut)

    // Load trigger fields if a model is already selected (edit mode)
    this.loadInitialTriggerFields()

    // Load dynamic record form fields for existing create_record actions
    this.loadExistingRecordForms()
  }

  loadInitialTriggerFields() {
    if (!this.fieldsUrlValue) return
    // Find the model select in the form (outside this component)
    const form = this.element.closest("form")
    if (!form) return
    const modelSelect = form.querySelector(
      '[name="automation_rule[target_model]"]',
    )
    if (modelSelect && modelSelect.value) {
      this.loadTriggerFields(modelSelect.value)
    }
  }

  loadExistingRecordForms() {
    if (!this.recordFormUrlValue) return

    const cards = this.actionsTarget.querySelectorAll(
      '.action-card[data-action-type="create_record"]',
    )
    cards.forEach((card) => {
      const modelSelect = card.querySelector('[data-field-name="model"]')
      if (modelSelect && modelSelect.value) {
        this.loadRecordFormFields(card, modelSelect.value)
      }
    })
  }

  // Called by rule-form controller when target model changes
  async loadTriggerFields(modelName) {
    if (!this.fieldsUrlValue || !modelName) return

    try {
      const url = `${this.fieldsUrlValue}?model=${encodeURIComponent(modelName)}`
      const response = await get(url, {
        responseKind: "json",
        credentials: "include",
      })

      if (response.ok) {
        const data = await response.json
        const fields = data.fields || []
        // Store full field objects (with association metadata) for the variable picker
        this._triggerFieldsFull = fields
        this.fieldsValue = fields.map((f) => [
          f.label ||
            f.name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
          f.name,
        ])
      }
    } catch (error) {
      console.error("Error loading trigger fields:", error)
    }
  }

  disconnect() {
    this.element.removeEventListener("focusout", this.handleFocusOut)

    const form = this.element.closest("form")
    if (form && this.handleFormSubmit) {
      form.removeEventListener("submit", this.handleFormSubmit)
    }

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  }

  // The action-selector modal is a Ui::ModalComponent (native <dialog>). Its
  // open/close/scroll-lock/Escape/focus-restore are owned by the ui--modal
  // controller on that same element — we drive it rather than reimplementing it.
  get modalController() {
    if (!this.hasModalTarget) return null
    return this.application.getControllerForElementAndIdentifier(
      this.modalTarget,
      "ui--modal",
    )
  }

  handleFormSubmit(_event) {
    this.updateOutput()
  }

  openModal() {
    if (this.disabledValue) return

    const currentCount =
      this.actionsTarget.querySelectorAll(".action-card").length
    if (currentCount >= this.maxActionsValue) {
      this.showError(`Maximum of ${this.maxActionsValue} actions allowed`)
      return
    }

    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ""
    }
    this.selectedCategory = CATEGORY_ALL
    this.updateCategoryButtons()
    this.performFilter()

    // Delegate open + scroll-lock + focus-restore to ui--modal (the search
    // input carries [autofocus], so it receives focus on open). Fall back to
    // the native <dialog> API if the modal controller hasn't connected yet.
    const modalController = this.modalController
    if (modalController) {
      modalController.open()
    } else if (typeof this.modalTarget.showModal === "function") {
      this.modalTarget.showModal()
    }

    setTimeout(() => {
      if (this.hasSearchInputTarget) {
        this.searchInputTarget.focus()
      }
    }, FOCUS_DELAY_MS)
  }

  closeModal() {
    const modalController = this.modalController
    if (modalController) {
      modalController.close()
    } else if (typeof this.modalTarget.close === "function") {
      this.modalTarget.close()
    }
  }

  selectCategory(event) {
    this.selectedCategory = event.currentTarget.dataset.category
    this.updateCategoryButtons()
    this.performFilter()
  }

  updateCategoryButtons() {
    // The buttons are Ui::SidebarAccordionComponent filter items; their active
    // styling is driven by the design-system `data-[active=true]:*` variants, so
    // we only flip `data-active` here (no DS class names live in JS).
    this.categoryButtonTargets.forEach((button) => {
      const isSelected = button.dataset.category === this.selectedCategory
      button.dataset.active = isSelected.toString()
      button.setAttribute("aria-pressed", isSelected.toString())
    })
  }

  filterActions() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    this.searchTimeout = setTimeout(() => {
      this.performFilter()
    }, SEARCH_DEBOUNCE_MS)
  }

  performFilter() {
    const searchTerm = this.hasSearchInputTarget
      ? this.searchInputTarget.value.toLowerCase().trim()
      : ""
    const buttons = this.actionsGridTarget.querySelectorAll(
      ".action-type-button",
    )
    let visibleCount = 0

    buttons.forEach((button) => {
      const category = button.dataset.category
      const label = button.dataset.label || ""
      const description = button.dataset.description || ""

      const categoryMatch =
        this.selectedCategory === CATEGORY_ALL ||
        category === this.selectedCategory
      const searchMatch =
        !searchTerm ||
        label.includes(searchTerm) ||
        description.includes(searchTerm)

      if (categoryMatch && searchMatch) {
        button.classList.remove("hidden")
        visibleCount++
      } else {
        button.classList.add("hidden")
      }
    })

    if (this.hasNoResultsTarget) {
      const grid = this.actionsGridTarget.querySelector(".grid")
      if (visibleCount === 0) {
        this.noResultsTarget.classList.remove("hidden")
        if (grid) grid.classList.add("hidden")
      } else {
        this.noResultsTarget.classList.add("hidden")
        if (grid) grid.classList.remove("hidden")
      }
    }
  }

  addAction(event) {
    if (this.disabledValue) return

    const actionType = event.currentTarget.dataset.actionType
    const actionMeta = this.actionTypesValue[actionType]

    if (!actionMeta) {
      this.showError(`Unknown action type: ${actionType}`)
      return
    }

    this.clearError()

    const card = this.buildActionCard(actionType)
    if (!card) {
      this.showError(`Unknown action type: ${actionType}`)
      return
    }
    this.actionsTarget.appendChild(card)

    this.reindexActions()
    this.hideEmptyState()
    this.closeModal()
    this.updateOutput()
  }

  // Clone the server-rendered <template> for this action type — it is built from
  // the SAME `_action_card` DS partial as existing cards, so every input/select/
  // textarea/button is a Ui::* component (no markup is hand-built here). The
  // "__IDX__" placeholder in field ids/names is swapped for a unique, monotonic
  // id so ids stay unique across cards; the numeric order + data-action-index are
  // fixed by reindexActions() after append. Tom Select on any <select>
  // auto-connects (ui--select) once the node lands in the DOM.
  buildActionCard(actionType) {
    const template = this.cardTemplateTargets.find(
      (t) => t.dataset.actionType === actionType,
    )
    if (!template) return null

    const fragment = template.content.cloneNode(true)
    const card = fragment.querySelector(".action-card")
    if (!card) return null

    const uid = (this._cardSeq = (this._cardSeq || 0) + 1)
    card
      .querySelectorAll(
        "[id], [name], [for], [aria-labelledby], [aria-controls], [aria-describedby]",
      )
      .forEach((el) => {
        for (const attr of [
          "id",
          "name",
          "for",
          "aria-labelledby",
          "aria-controls",
          "aria-describedby",
        ]) {
          const val = el.getAttribute(attr)
          if (val && val.includes("__IDX__")) {
            el.setAttribute(attr, val.replaceAll("__IDX__", uid))
          }
        }
      })

    return card
  }

  // ui--modal controller driving the shared remove-action confirmation dialog.
  get deleteModalController() {
    if (!this.hasDeleteModalTarget) return null
    return this.application.getControllerForElementAndIdentifier(
      this.deleteModalTarget,
      "ui--modal",
    )
  }

  // Trash button → remember the target card and open the confirmation modal.
  // The ref is overwritten on every prompt, so a cancelled prompt can't leave
  // a stale card behind (confirm is only reachable via a fresh open).
  promptRemoveAction(event) {
    if (this.disabledValue) return

    const card = event.currentTarget.closest(".action-card")
    if (!card) return
    this.pendingRemovalCard = card

    const controller = this.deleteModalController
    if (controller) {
      controller.open()
    } else if (
      this.hasDeleteModalTarget &&
      typeof this.deleteModalTarget.showModal === "function"
    ) {
      this.deleteModalTarget.showModal()
    } else {
      // No modal available (unexpected) — fall back to immediate removal.
      this.performRemoval(card)
    }
  }

  // Confirm button in the modal → remove the remembered card, then close.
  confirmRemoveAction() {
    const card = this.pendingRemovalCard
    this.pendingRemovalCard = null
    if (card) this.performRemoval(card)

    const controller = this.deleteModalController
    if (controller) {
      controller.close()
    } else if (
      this.hasDeleteModalTarget &&
      typeof this.deleteModalTarget.close === "function"
    ) {
      this.deleteModalTarget.close()
    }
  }

  performRemoval(card) {
    if (!card) return
    card.remove()
    this.reindexActions()
    this.checkEmptyState()
    this.updateOutput()
  }

  moveUp(event) {
    if (this.disabledValue) return

    const card = event.currentTarget.closest(".action-card")
    if (!card) return
    const prev = card.previousElementSibling
    if (prev && prev.classList.contains("action-card")) {
      card.parentNode.insertBefore(card, prev)
      this.reindexActions()
      this.updateOutput()
    }
  }

  moveDown(event) {
    if (this.disabledValue) return

    const card = event.currentTarget.closest(".action-card")
    if (!card) return
    const next = card.nextElementSibling
    if (next && next.classList.contains("action-card")) {
      card.parentNode.insertBefore(next, card)
      this.reindexActions()
      this.updateOutput()
    }
  }

  configChanged(event) {
    const field = event?.target
    if (field) {
      const card = field.closest(".action-card")
      if (card && card.dataset.actionType === "create_record") {
        // When model_select changes, load dynamic form fields and clear JSON
        if (field.dataset.fieldName === "model" && this.recordFormUrlValue) {
          const attributesTextarea = card.querySelector(
            '[data-field-name="attributes"]',
          )
          if (attributesTextarea) attributesTextarea.value = ""
          this.loadRecordFormFields(card, field.value)
        }
        // When any record form field changes, sync to the attributes textarea
        if (field.closest(".record-form-fields")) {
          this.syncRecordFormToJson(card)
        }
      }
    }
    this.updateOutput()
  }

  // ── Inline {{expression}} validation ─────────────────────────────────────
  // Delegated focusout: any config input whose value contains a template
  // expression gets validated server-side and flagged inline.

  handleFocusOut(event) {
    const input = event.target
    if (!this.validateUrlValue) return
    if (!input.matches("input, textarea")) return

    const card = input.closest(".action-card")
    if (!card) return

    this._clearExpressionError(input)
    if (!input.value.includes("{{")) return

    this._validateExpressionInput(input, card)
  }

  async _validateExpressionInput(input, card) {
    const model = this.element
      .closest("form")
      ?.querySelector('[name="automation_rule[target_model]"]')?.value
    if (!model) return

    try {
      const body = new FormData()
      body.append("value", input.value)
      body.append("target_model", model)
      body.append("action_index", card.dataset.actionIndex || 0)

      const response = await post(this.validateUrlValue, {
        body,
        responseKind: "json",
      })
      if (!response.ok) return

      const data = await response.json
      if (data.valid) return

      this._showExpressionError(input, data.errors)
    } catch (_e) {
      // Validation is advisory — never block on network errors
    }
  }

  _showExpressionError(input, errors) {
    input.classList.add("border-danger!")

    const note = document.createElement("p")
    note.className = "expression-error text-xs text-danger mt-1"
    note.textContent = errors
      .map((error) => `{{${error.expression}}}: ${error.message}`)
      .join(" · ")
    input.insertAdjacentElement("afterend", note)
  }

  _clearExpressionError(input) {
    input.classList.remove("border-danger!")
    const sibling = input.nextElementSibling
    if (sibling?.classList.contains("expression-error")) sibling.remove()
  }

  syncRecordFormToJson(card) {
    const recordForm = card.querySelector(".record-form-fields")
    const attributesTextarea = card.querySelector(
      '[data-field-name="attributes"]',
    )
    if (!recordForm || !attributesTextarea) return

    const attributes = {}
    const inputs = recordForm.querySelectorAll("[data-field-name]")
    inputs.forEach((input) => {
      const name = input.dataset.fieldName
      const value =
        input.type === "checkbox"
          ? input.checked
            ? "true"
            : "false"
          : input.value
      if (name && value !== "") {
        attributes[name] = value
      }
    })

    attributesTextarea.value = JSON.stringify(attributes, null, 2)
  }

  async loadRecordFormFields(card, modelName) {
    const container = card.querySelector(".record-form-container")
    const advancedField = card.querySelector(
      '.action-field[data-field-name-wrapper="attributes"]',
    )

    if (!container) return

    if (!modelName) {
      container.innerHTML =
        '<p class="text-sm text-foreground-medium">Select a record type to see available fields.</p>'
      if (advancedField) advancedField.classList.remove("hidden")
      return
    }

    // Show loading state
    container.innerHTML =
      '<p class="text-sm text-foreground-medium animate-pulse">Loading fields...</p>'

    try {
      const separator = this.recordFormUrlValue.includes("?") ? "&" : "?"
      const url = `${this.recordFormUrlValue}${separator}model=${encodeURIComponent(modelName)}`

      const response = await get(url, {
        responseKind: "html",
        credentials: "include",
      })

      if (response.ok && !response.redirected) {
        const html = await response.html
        // Ensure we got a partial, not a full page
        if (html.includes("<!DOCTYPE") || html.includes("<html")) {
          container.innerHTML =
            '<p class="text-sm text-danger">Unexpected response. Please reload the page.</p>'
          if (advancedField) advancedField.classList.remove("hidden")
          return
        }
        container.innerHTML = html

        // Make IDs unique per action card to avoid duplicates in the DOM
        const actionIndex = card.dataset.actionIndex || "0"
        container.querySelectorAll("[id]").forEach((el) => {
          const oldId = el.id
          el.id = `${oldId}-action-${actionIndex}`
          // Update any label pointing to the old ID
          container
            .querySelectorAll(`label[for="${oldId}"]`)
            .forEach((label) => {
              label.setAttribute("for", el.id)
            })
        })

        // Populate form fields from existing attributes JSON
        const attributesTextarea = card.querySelector(
          '[data-field-name="attributes"]',
        )
        if (attributesTextarea && attributesTextarea.value) {
          try {
            const attrs = JSON.parse(attributesTextarea.value)
            const recordInputs = container.querySelectorAll("[data-field-name]")
            recordInputs.forEach((input) => {
              const name = input.dataset.fieldName
              if (name && attrs[name] !== undefined) {
                if (input.type === "checkbox") {
                  input.checked = attrs[name] === "true"
                } else {
                  input.value = attrs[name]
                }
              }
            })
          } catch {
            // Invalid JSON, leave fields empty
          }
        }

        // Hide JSON textarea when form fields loaded, add toggle
        if (advancedField) {
          advancedField.classList.add("hidden")

          if (!card.querySelector(".advanced-toggle")) {
            const toggle = document.createElement("button")
            toggle.type = "button"
            toggle.className =
              "advanced-toggle text-xs text-primary hover:underline mt-3 block"
            toggle.textContent = "Advanced: Edit as JSON"
            toggle.addEventListener("click", () => {
              const isHidden = advancedField.classList.contains("hidden")
              advancedField.classList.toggle("hidden")
              toggle.textContent = isHidden
                ? "Hide advanced JSON"
                : "Advanced: Edit as JSON"
            })
            container.after(toggle)
          }
        }
      } else {
        container.innerHTML =
          '<p class="text-sm text-danger">Failed to load form fields.</p>'
        if (advancedField) advancedField.classList.remove("hidden")
      }
    } catch {
      container.innerHTML =
        '<p class="text-sm text-danger">Error loading form fields.</p>'
      if (advancedField) advancedField.classList.remove("hidden")
    }
  }

  async browseAssociation(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const model = button.dataset.model
    const field = button.dataset.field
    const wrapper = button.closest(".relative")

    if (!model || !field || !wrapper) return

    // Close any existing browse dropdown
    const existing = wrapper.querySelector(".association-browse-dropdown")
    if (existing) {
      existing.remove()
      return
    }

    // Find the text input for this field
    const input = wrapper.querySelector(`[data-field-name="${field}"]`)
    if (!input) return

    // Determine URL base
    let baseUrl = this.associationRecordsUrlValue
    if (!baseUrl && this.recordFormUrlValue) {
      baseUrl = this.recordFormUrlValue.replace(
        /\/record_form$/,
        "/association_records",
      )
    }
    if (!baseUrl) return

    // Show loading dropdown
    const dropdown = document.createElement("div")
    dropdown.className =
      "association-browse-dropdown absolute right-0 top-full mt-1 z-50 w-full max-h-64 overflow-y-auto rounded-lg border border-border bg-card shadow-lg"
    dropdown.innerHTML =
      '<div class="px-3 py-2 text-sm text-foreground-medium animate-pulse">Loading records...</div>'
    wrapper.appendChild(dropdown)

    try {
      const url = `${baseUrl}?model=${encodeURIComponent(model)}&field=${encodeURIComponent(field)}`
      const response = await get(url, {
        responseKind: "json",
        credentials: "include",
      })

      if (response.ok) {
        const data = await response.json
        if (data.success && data.records && data.records.length > 0) {
          const list = document.createElement("div")
          list.className = "py-1"

          data.records.forEach((record) => {
            const item = document.createElement("button")
            item.type = "button"
            item.className =
              "w-full flex items-center justify-between px-3 py-1.5 text-sm text-foreground hover:bg-card-secondary transition-colors text-left"
            item.dataset.recordId = record.id
            item.dataset.recordLabel = record.label

            const label = document.createElement("span")
            label.textContent = record.label
            label.className = "truncate"

            const id = document.createElement("code")
            id.textContent = record.id.substring(0, 8) + "..."
            id.className =
              "text-xs text-foreground-medium font-mono ml-2 shrink-0"

            item.appendChild(label)
            item.appendChild(id)

            item.addEventListener("click", () => {
              // Insert at cursor position (like variable tokens) instead of replacing
              const start = input.selectionStart || 0
              const end = input.selectionEnd || 0
              const before = input.value.substring(0, start)
              const after = input.value.substring(end)

              input.value = before + record.id + after
              input.focus()

              const newPos = start + record.id.length
              input.setSelectionRange(newPos, newPos)

              input.dispatchEvent(new Event("input", { bubbles: true }))
              dropdown.remove()
            })

            list.appendChild(item)
          })

          dropdown.innerHTML = ""
          dropdown.appendChild(list)
        } else {
          dropdown.innerHTML =
            '<div class="px-3 py-2 text-sm text-foreground-medium">No records found.</div>'
        }
      } else {
        dropdown.innerHTML =
          '<div class="px-3 py-2 text-sm text-danger">Failed to load records.</div>'
      }
    } catch (error) {
      console.error("Error loading association records:", error)
      dropdown.innerHTML =
        '<div class="px-3 py-2 text-sm text-danger">Error loading records.</div>'
    }

    // Close on outside click
    const closeHandler = (e) => {
      if (!dropdown.contains(e.target) && e.target !== button) {
        dropdown.remove()
        document.removeEventListener("click", closeHandler)
      }
    }
    setTimeout(() => document.addEventListener("click", closeHandler), 0)
  }

  reindexActions() {
    const cards = this.actionsTarget.querySelectorAll(".action-card")
    cards.forEach((card, index) => {
      card.dataset.actionIndex = index
      const orderSpan = card.querySelector(".action-order")
      if (orderSpan) {
        orderSpan.textContent = `Action #${index + 1}`
      }
    })
  }

  updateOutput() {
    const actions = this.serializeActions()
    this.outputTarget.value = JSON.stringify(actions)

    this.element.dispatchEvent(
      new CustomEvent("action-builder:change", {
        detail: { value: actions },
        bubbles: true,
      }),
    )
  }

  serializeActions() {
    const cards = this.actionsTarget.querySelectorAll(".action-card")
    const actions = []

    cards.forEach((card, index) => {
      const actionType = card.dataset.actionType
      const config = {}

      // Collect standard (top-level) action fields. `data-action-field` marks
      // them across both render paths — the server partial's DS components and
      // the JS-built cards — and excludes nested record-form inputs (which
      // carry only data-field-name and are collected separately below).
      const fields = card.querySelectorAll("[data-action-field]")
      fields.forEach((field) => {
        const name = field.dataset.fieldName
        const value = field.value
        if (name && value) {
          config[name] = value
        }
      })

      // Collect dynamic record form fields into config.attributes as JSON
      const recordFormContainer = card.querySelector(".record-form-fields")
      if (recordFormContainer) {
        const attributes = {}
        const recordInputs =
          recordFormContainer.querySelectorAll("[data-field-name]")
        recordInputs.forEach((input) => {
          const name = input.dataset.fieldName
          let value
          if (input.type === "checkbox") {
            value = input.checked ? "true" : "false"
          } else {
            value = input.value
          }
          if (name && value) {
            attributes[name] = value
          }
        })
        if (Object.keys(attributes).length > 0) {
          config["attributes"] = JSON.stringify(attributes)
        }
      }

      const preserved = card.dataset.preservedConfig
      const stored = preserved
        ? { action_type: actionType, config: this.parsePreservedConfig(preserved) }
        : this.persistedForm(actionType, config)

      actions.push({
        action_type: stored.action_type,
        execution_order: index,
        config: stored.config,
      })
    })

    return actions
  }

  // An unparseable preserved config must not break serializing every other
  // card; that one action saves with an empty config instead.
  parsePreservedConfig(preserved) {
    try {
      return JSON.parse(preserved)
    } catch {
      return {}
    }
  }

  // A catalog card may save as another action type (`persist_as`): its fixed
  // config is merged in and its own fields are nested under `fields_key` when
  // one is given. The component maps the stored form back to the card.
  persistedForm(actionType, config) {
    const persistAs = this.actionTypesValue[actionType]?.persist_as
    if (!persistAs) return { action_type: actionType, config }

    const fields = persistAs.fields_key
      ? { [persistAs.fields_key]: config }
      : config
    return {
      action_type: persistAs.action_type,
      config: { ...fields, ...(persistAs.config || {}) },
    }
  }

  hideEmptyState() {
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.add("hidden")
    }
  }

  checkEmptyState() {
    if (this.hasEmptyStateTarget && this.hasActionsTarget) {
      const hasActions =
        this.actionsTarget.querySelectorAll(".action-card").length > 0
      this.emptyStateTarget.classList.toggle("hidden", hasActions)
    }
  }

  showError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.classList.remove("hidden")
    }
  }

  clearError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = ""
      this.errorMessageTarget.classList.add("hidden")
    }
  }

  // Drag-to-reorder is handled by the ui--draggable-list controller on the
  // actions container; it reorders the cards in the DOM and fires `reorder`.
  // We just renumber ("Action #N" + data-action-index) and reserialize.
  onReorder() {
    if (this.disabledValue) return
    this.reindexActions()
    this.updateOutput()
  }

  toggleVariablePicker(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const wrapper = button.closest(".relative")

    // Close any other open pickers
    this.element.querySelectorAll(".variable-picker-dropdown").forEach((d) => {
      if (d.parentElement !== wrapper) d.remove()
    })

    // Toggle existing dropdown
    const existing = wrapper.querySelector(".variable-picker-dropdown")
    if (existing) {
      existing.remove()
      return
    }

    // Collect variable sections with cascading context
    const sections = this.buildVariableSections(button)
    if (sections.length === 0) return

    // Build dropdown
    const dropdown = document.createElement("div")
    dropdown.className =
      "variable-picker-dropdown absolute right-0 top-full mt-1 z-50 w-auto max-h-64 overflow-y-auto rounded-lg border border-border bg-card shadow-lg"

    const list = document.createElement("div")
    list.className = "py-1"

    sections.forEach((section) => {
      // Section header
      const header = document.createElement("div")
      header.className =
        "px-3 py-1.5 text-xs font-semibold text-foreground-medium uppercase tracking-wider bg-card-secondary border-b border-border"
      header.textContent = section.title
      list.appendChild(header)

      // Section items
      section.fields.forEach((field) => {
        this._renderVariableItem(list, field, 0)
      })
    })

    dropdown.appendChild(list)
    wrapper.appendChild(dropdown)

    // Close on outside click
    const closeHandler = (e) => {
      if (!dropdown.contains(e.target) && e.target !== button) {
        dropdown.remove()
        document.removeEventListener("click", closeHandler)
      }
    }
    setTimeout(() => document.addEventListener("click", closeHandler), 0)
  }

  // Build variable sections: trigger fields + cascading previous action fields
  // Fields with belongs_to associations include an `association` object with nested fields
  buildVariableSections(button) {
    const sections = []
    const currentCard = button.closest(".action-card")
    const currentIndex = currentCard
      ? parseInt(currentCard.dataset.actionIndex, 10)
      : -1

    // Section 1: Trigger record fields (always from the rule's target model)
    // Use full field objects (with association metadata) when available
    const fullFields = this._triggerFieldsFull || []
    let triggerFields = []
    if (fullFields.length > 0) {
      triggerFields = fullFields.map((f) => ({
        name: f.name,
        label:
          f.label ||
          f.name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        variable: f.name,
        association: f.association || null,
      }))
    } else if (this.fieldsValue.length > 0) {
      triggerFields = this.fieldsValue.map(([label, name]) => ({
        name,
        label,
        variable: name,
        association: null,
      }))
    }
    if (triggerFields.length > 0) {
      sections.push({
        title: "Trigger Record",
        fields: triggerFields,
      })
    }

    // Section 2+: Previous action fields (cascading)
    if (currentIndex > 0) {
      const allCards = this.actionsTarget.querySelectorAll(".action-card")
      allCards.forEach((card) => {
        const cardIndex = parseInt(card.dataset.actionIndex, 10)
        if (cardIndex >= currentIndex) return

        const actionType = card.dataset.actionType
        const actionLabel =
          card.querySelector(".action-type-label")?.textContent || actionType
        const prefix = `action_${cardIndex + 1}`
        const actionFields = this.extractFieldsFromCard(card)

        if (actionFields.length > 0) {
          sections.push({
            title: `Action #${cardIndex + 1}: ${actionLabel}`,
            fields: actionFields.map((f) => ({
              name: f.name,
              label: f.label,
              variable: `${prefix}.${f.name}`,
              association: null,
            })),
          })
        }
      })
    }

    return sections
  }

  // Extract available fields from an action card
  extractFieldsFromCard(card) {
    const fields = []
    const actionType = card.dataset.actionType

    // For create_record: use the record-form-fields available fields or the loaded form inputs
    if (actionType === "create_record") {
      const recordForm = card.querySelector(".record-form-fields")
      if (recordForm) {
        try {
          const available = JSON.parse(
            recordForm.dataset.availableFields || "[]",
          )
          if (available.length > 0) return available
        } catch {
          // fall through
        }
        // Fallback: extract from rendered form inputs
        const inputs = recordForm.querySelectorAll("[data-field-name]")
        inputs.forEach((input) => {
          const name = input.dataset.fieldName
          if (name) {
            fields.push({
              name,
              label: name
                .replace(/_/g, " ")
                .replace(/\b\w/g, (c) => c.toUpperCase()),
            })
          }
        })
        return fields
      }
    }

    // For other action types: extract from the top-level config field elements
    const inputs = card.querySelectorAll("[data-action-field]")
    inputs.forEach((input) => {
      const name = input.dataset.fieldName
      if (name) {
        fields.push({
          name,
          label: name
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase()),
        })
      }
    })

    return fields
  }

  // Render a single variable item in the picker, with collapsible association support
  _renderVariableItem(container, field, depth) {
    const assoc = field.association
    const paddingLeft = 12 + depth * 16

    if (assoc) {
      // This field is a FK with a belongs_to — skip the raw FK field,
      // show only the expandable association group (which includes the id inside)
      const assocName = assoc.name
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase())
      const toggle = document.createElement("button")
      toggle.type = "button"
      toggle.className =
        "w-full flex items-center gap-1.5 py-1.5 text-xs font-medium text-primary hover:bg-primary/5 transition-colors text-left"
      toggle.style.paddingLeft = `${paddingLeft}px`
      toggle.style.paddingRight = "12px"

      const chevron = document.createElement("span")
      chevron.textContent = "\u25B6"
      chevron.className = "text-[10px] transition-transform duration-150"
      chevron.style.display = "inline-block"

      const toggleLabel = document.createElement("span")
      toggleLabel.textContent = assocName

      const badge = document.createElement("span")
      badge.textContent = `${assoc.fields.length} fields`
      badge.className = "ml-auto text-[10px] text-foreground-medium font-normal"

      toggle.appendChild(chevron)
      toggle.appendChild(toggleLabel)
      toggle.appendChild(badge)
      container.appendChild(toggle)

      // Hidden container for nested fields
      const nestedContainer = document.createElement("div")
      nestedContainer.className = "hidden"
      container.appendChild(nestedContainer)

      // Render nested association fields into the hidden container
      assoc.fields.forEach((nestedField) => {
        this._renderVariableItem(
          nestedContainer,
          {
            name: nestedField.name,
            label: nestedField.label,
            variable: nestedField.name,
            association: nestedField.association || null,
          },
          depth + 1,
        )
      })

      // Toggle expand/collapse on click
      toggle.addEventListener("click", (e) => {
        e.preventDefault()
        e.stopPropagation()
        const isHidden = nestedContainer.classList.contains("hidden")
        nestedContainer.classList.toggle("hidden")
        chevron.style.transform = isHidden ? "rotate(90deg)" : ""
      })
    } else {
      // Regular field — simple insertable item
      const item = document.createElement("button")
      item.type = "button"
      item.className =
        "w-full flex items-center justify-between py-1.5 text-sm text-foreground hover:bg-card-secondary transition-colors text-left"
      item.style.paddingLeft = `${paddingLeft}px`
      item.style.paddingRight = "12px"
      item.dataset.action =
        "click->ui--automation--action-builder#insertVariable"
      item.dataset.variableName = field.variable

      const label = document.createElement("span")
      label.textContent =
        field.label ||
        field.name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
      label.className = "truncate"

      const code = document.createElement("code")
      code.textContent = `{{${field.variable}}}`
      code.className = "text-xs text-foreground-medium font-mono ml-2 shrink-0"

      item.appendChild(label)
      item.appendChild(code)
      container.appendChild(item)
    }
  }

  insertVariable(event) {
    event.preventDefault()
    event.stopPropagation()

    const variableName = event.currentTarget.dataset.variableName
    if (!variableName) return

    const wrapper = event.currentTarget.closest(".relative")
    if (!wrapper) return

    // Find the input or textarea inside the wrapper
    const input = wrapper.querySelector(
      "input[data-field-name], textarea[data-field-name]",
    )
    if (!input) return

    const insertion = `{{${variableName}}}`
    const start = input.selectionStart || 0
    const end = input.selectionEnd || 0
    const before = input.value.substring(0, start)
    const after = input.value.substring(end)

    input.value = before + insertion + after
    input.focus()

    // Place cursor after the inserted variable
    const newPos = start + insertion.length
    input.setSelectionRange(newPos, newPos)

    // Close dropdown
    const dropdown = wrapper.querySelector(".variable-picker-dropdown")
    if (dropdown) dropdown.remove()

    // Trigger change event to sync JSON
    input.dispatchEvent(new Event("input", { bubbles: true }))

    // Offer pipe functions for the just-inserted token — users should never
    // have to type "| add_days:180" by hand
    this._openPipeMenu(wrapper, input, newPos)
  }

  // ── Pipe function chip menu ──────────────────────────────────────────────
  // Appends `| fn` / `| fn:arg` INSIDE the token that ends at tokenEnd.
  // Positions are only trusted while the menu is open (any outside click closes it).

  _openPipeMenu(wrapper, input, tokenEnd) {
    if (!this.pipeFunctionsValue.length) return
    this._closePipeMenu()

    this._pipeInput = input
    this._pipeTokenEnd = tokenEnd

    const labels = this.pipeMenuLabelsValue
    const menu = document.createElement("div")
    menu.className =
      "pipe-menu-dropdown absolute right-0 top-full mt-1 z-50 w-72 max-h-64 overflow-y-auto rounded-lg border border-border bg-card shadow-lg"

    const header = document.createElement("div")
    header.className =
      "flex items-center justify-between px-3 py-1.5 text-xs font-semibold text-foreground-medium uppercase tracking-wider bg-card-secondary border-b border-border sticky top-0"
    const title = document.createElement("span")
    title.textContent = labels.title || "Add a function"
    const done = document.createElement("button")
    done.type = "button"
    done.className =
      "text-primary hover:text-primary/80 normal-case font-medium"
    done.textContent = labels.done || "Done"
    done.addEventListener("click", () => this._closePipeMenu())
    header.append(title, done)
    menu.appendChild(header)

    const categories = new Map()
    this.pipeFunctionsValue.forEach((fn) => {
      if (!categories.has(fn.category)) categories.set(fn.category, [])
      categories.get(fn.category).push(fn)
    })

    categories.forEach((fns, category) => {
      const label = document.createElement("div")
      label.className =
        "px-3 pt-2 pb-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider"
      label.textContent = category
      menu.appendChild(label)

      const row = document.createElement("div")
      row.className = "flex flex-wrap gap-1 px-3 pb-2"
      fns.forEach((fn) => row.appendChild(this._buildPipeChip(fn, menu)))
      menu.appendChild(row)
    })

    wrapper.appendChild(menu)

    const closeHandler = (e) => {
      if (!menu.contains(e.target)) {
        this._closePipeMenu()
        document.removeEventListener("click", closeHandler)
      }
    }
    this._pipeMenuCloseHandler = closeHandler
    setTimeout(() => document.addEventListener("click", closeHandler), 0)
  }

  _buildPipeChip(fn, menu) {
    const chip = document.createElement("button")
    chip.type = "button"
    chip.className =
      "pipe-chip px-2 py-0.5 text-xs font-mono rounded-full border border-input bg-card hover:bg-card-secondary hover:border-primary text-foreground-medium hover:text-primary transition-colors"
    chip.textContent = fn.name
    chip.title = fn.description || ""
    chip.dataset.pipeName = fn.name

    chip.addEventListener("click", (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (fn.args === 0) {
        this._appendPipe(fn.name, null)
      } else {
        this._showPipeArgInput(fn, menu)
      }
    })

    return chip
  }

  _showPipeArgInput(fn, menu) {
    menu.querySelector(".pipe-arg-row")?.remove()

    const labels = this.pipeMenuLabelsValue
    const row = document.createElement("div")
    row.className =
      "pipe-arg-row flex items-center gap-2 px-3 py-2 border-t border-border bg-card-secondary sticky bottom-0"

    const name = document.createElement("code")
    name.className = "text-xs font-mono text-foreground"
    name.textContent = `${fn.name}:`

    const argInput = document.createElement("input")
    argInput.type = "text"
    argInput.className =
      "pipe-arg-input flex-1 min-w-0 px-2 py-1 text-xs font-mono rounded border border-input bg-card"
    argInput.placeholder = fn.example || labels.argument_placeholder || "value"

    const apply = document.createElement("button")
    apply.type = "button"
    apply.className =
      "pipe-arg-apply px-2 py-1 text-xs font-medium rounded bg-primary-500 text-white hover:bg-primary-600"
    apply.textContent = labels.apply || "Apply"

    const submit = () => {
      const arg = argInput.value.trim()
      if (!arg) return
      this._appendPipe(fn.name, arg)
      row.remove()
    }
    apply.addEventListener("click", (e) => {
      e.preventDefault()
      e.stopPropagation()
      submit()
    })
    argInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault()
        submit()
      }
    })

    row.append(name, argInput, apply)
    menu.appendChild(row)
    argInput.focus()
  }

  _appendPipe(name, arg) {
    const input = this._pipeInput
    if (!input) return

    const insertion = arg ? ` | ${name}:${arg}` : ` | ${name}`
    const at = this._pipeTokenEnd - 2 // just before the closing "}}"
    input.value = input.value.slice(0, at) + insertion + input.value.slice(at)
    this._pipeTokenEnd += insertion.length

    input.dispatchEvent(new Event("input", { bubbles: true }))
  }

  _closePipeMenu() {
    this.element
      .querySelectorAll(".pipe-menu-dropdown")
      .forEach((m) => m.remove())
    if (this._pipeMenuCloseHandler) {
      document.removeEventListener("click", this._pipeMenuCloseHandler)
      this._pipeMenuCloseHandler = null
    }
  }
}
